/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External/Third-party dependencies
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Internal dependencies
import useStyleSystem from '@rakuten/hooks/useStyleSystem';
import { View } from '@rakuten/modules-core-components';


// -----------------------------------------------------------------------------
// STYLES
// -----------------------------------------------------------------------------

/**
 * Calculate styles for this component, optionally based on props, theme,
 * currently matching breakpoint etc.
 *
 * @param {object} args Arguments used for style calculation
 * @param {object} args.props Current props passed to the component
 * @param {object} args.theme Current theme object
 * @return {object} Object that maps style names to style objects
 */
function createStyles({ props, theme }) {
  return {
    blocker: {
      backgroundColor: props.transparent
        ? theme.colors.transparent
        : theme.colors.black16,
      bottom: 0,
      height: `calc(var(--vh, 1vh) * 100)`,
      left: 0,
      position: 'fixed', // changed from 'absolute' to 'fixed' because of this bug: https://app.clickup.com/t/45031420/KWA-14494
      right: 0,
      top: 0,
      width: '100vw',
      zIndex: theme.variables.zIndexModalBlocker
    }
  };
}

// ---------------------------------------------------------------------------
// EVENT HANDLING
// ---------------------------------------------------------------------------

/**
 * Invoked when a scrolling or pinching action (wheel) is triggered.
 *
 * @param {Event} event The event object representing the mouse or touch event
 */

function handleBrowserDefaultZoom(event) {
  // To distinguish 'pinch' from normal scrolling on the touchpad:
  // When the Ctrl key is pressed, the event is a pinch.
  // In some browsers the mousepad 'pinch' is not registered as a 'wheel' event
  // but a 'touchmove' event. If this 'touchmove' is a 'pinch' - with two fingers -
  // the size 'event.touches' object is more than one.
  // Only for zoom - touchpad pinch - a preventDefault should happen
  // Resolves: KWA-13874 & KWA-13879 & KWA-14066
  // https://app.clickup.com/t/45031420/KWA-13874
  // https://app.clickup.com/t/45031420/KWA-13879
  // https://app.clickup.com/t/45031420/KWA-14066
  if ((event.ctrlKey || (event.touches && Object.keys(event.touches).length > 1))) {
    event.preventDefault();
  }
}


// -----------------------------------------------------------------------------
// COMPONENT
// -----------------------------------------------------------------------------

function ModalBlocker(props) {
  // "Hook" this component into the theme/style system
  const [ styles ] = useStyleSystem(
    {
      id: 'ModalBlocker',
      props,
      styleFn: createStyles
    }
  );

  const { trapFocus } = props;
  const modelBlockerRef = useRef( null );

  // ---------------------------------------------------------------------------
  // EVENT HANDLER
  // ---------------------------------------------------------------------------

  function onPress(event) {
    if (props.onPress) {
      // We want to make sure that the ModalBlocker is actually the element that
      // has been pressed, and not some element on top of the modal blocker.
      // This will prevent that the modal blocker is closed because the event
      // from the other element bubbles up to the modal blocker element and is
      // additionally handled there.
      const target = event.target || event.nativeEvent.target || event.nativeEvent.originalTarget;
      if (target && target.classList?.length > 0) {
        const index = Array.from(target.classList).findIndex(cls => cls.startsWith('ModalBlocker_blocker'));
        if (index > -1) {
          props.onPress();
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // HOOKS
  // ---------------------------------------------------------------------------

  useEffect( () => {
    if ( props.preventZooming ) {
      document.querySelector( 'html' ).style.touchAction = 'none';
      document.body.style.touchAction = 'none';

      window.addEventListener( 'wheel', handleBrowserDefaultZoom, { passive: false } );
      window.addEventListener( 'touchmove', handleBrowserDefaultZoom, { passive: false } );
      window.addEventListener( 'touchstart', handleBrowserDefaultZoom, { passive: false } );

      return () => {
        document.querySelector( 'html' ).style.touchAction = null;
        document.body.style.touchAction = null;

        window.removeEventListener( 'wheel', handleBrowserDefaultZoom, { passive: false } );
        window.removeEventListener( 'touchmove', handleBrowserDefaultZoom, { passive: false } );
        window.removeEventListener( 'touchstart', handleBrowserDefaultZoom, { passive: false } );
      };
    }
  }, [] );

  const container = modelBlockerRef.current;
  const focusableSelector = 'button, input, [role]:not([tabindex="-1"]):not([role="img"]), [tabindex]:not([tabindex="-1"])';
  const focusableElements = container?.querySelectorAll( focusableSelector );

  let firstFocusableElement, lastFocusableElement;

  if (focusableElements && focusableElements?.length > 0) {
    firstFocusableElement = focusableElements[0];
    lastFocusableElement = [...focusableElements].at(-1);
  }

  /**
   * Event listener for keyboard navigation (using Tab)
   * If trapFocus === true, we need to let the focus stay inside the container
   * KWA-14636
   *
   * @param {Event} e The keydown object
   */
  const focusHandler = ( e ) => {
    const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

    if ( !isTabPressed ) { return; }

    if ( !focusableElements ) {
      e.preventDefault();
    }

    if (e.shiftKey) { // if shift + tab
      if (document.activeElement === firstFocusableElement) {
        e.preventDefault();
        lastFocusableElement.focus();
      }
    } else { // if tab
      if (document.activeElement === lastFocusableElement) {
        e.preventDefault();
        firstFocusableElement.focus();
      }
    }
  };

  // Monitoring whether the new focus target is outside the container
  // If so, we switch the focus back to previous target
  // or to the first focusable element within the container
  const preventFocusLoose = ( event ) => {
    const newFocus = event.relatedTarget;
    const currentFocus = event.target;

    if (newFocus && !container.contains(newFocus)) {
      event.preventDefault();
      currentFocus ? currentFocus.focus() : firstFocusableElement.focus();
    }
  };


  useEffect( () => {
    if ( !trapFocus ) { return; }

    window.addEventListener( 'keydown', focusHandler, { passive: false } );

    return () => {
      window.removeEventListener( 'keydown', focusHandler, { passive: false } );
    };
  }, [ focusHandler, trapFocus ] );


  useEffect( () => {
    container?.addEventListener('focusout', preventFocusLoose);

    return () => {
      window.removeEventListener( 'focusout', preventFocusLoose );
    };
  }, [container] );


  // On modal close we need to focus on the content [KWA-14636]
  // iframe[aria-hidden="false"] selector works for both UR and Teng engines
  const focusContentOnUnmount = () => {
    const epubContainer = document.querySelector( '.EPub_engineContainer' );
    const currentIframe = Array.from(epubContainer.querySelectorAll( 'iframe[aria-hidden="false"]' )).at(-1);

    currentIframe?.focus();
  }


  useEffect( () => {
    return () => { focusContentOnUnmount(); };
  }, [] );


  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <View
      onPress={ onPress }
      style={styles.blocker}
      testID={props.testID}
      ref={modelBlockerRef}
    >
      { props.children }
    </View>
  );
}


// -----------------------------------------------------------------------------
// PROPS VALIDATION
// -----------------------------------------------------------------------------

ModalBlocker.propTypes = {
  /** Function to execute when the modal blocker is clicked/tapped [optional] */
  onPress: PropTypes.func,

  /** Whether or not the browser default zoom function should be blocked [optional] */
  preventZooming: PropTypes.bool,

  /** ID for test automation [optional] */
  testID: PropTypes.string,

  /** Whether or not the blocker should be transparent [optional] */
  transparent: PropTypes.bool,

  /** If true, inner container will get focus on load and keep it until unmounted [optional] */
  trapFocus: PropTypes.bool
};

ModalBlocker.defaultProps = {
  preventZooming: false,
  transparent: false,
  trapFocus: true
};


// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export default ModalBlocker;
